// ADR 0023 Sprint 7 Wave B — Per-tenant versioned settings API.
//
// Generic fetchers for all 6 settings domains. Mirrors the HTTP surface
// defined in internal/api/tenant_settings_handler.go.
//
// Path convention: paths passed to apiJson() must NOT include /api/v1.
// Route segment {tenant_id} is the numeric BIGINT PK (NOT the UUID public_id).
// This matches the backend comment: "The :tenant_id segment is the numeric BIGINT
// primary key from public.tenants (NOT the UUID public_id; NOT the slug)."
//
// Domain slugs match the backend route registrations exactly:
//   banking | fraud | aml | case-workflow | notification | reporting-retention
//
// Approval entity types for the activate flow are sourced from
// internal/api/tenant_settings_handler.go approvalEntityType() per domain and
// the entities.ApprovalEntityType constants from internal/entities/approval.go.
//
// Security:
//   - Settings payloads may contain PII for the notification domain (AF-007).
//   - Never log payload values.
//   - The tenant_id in the path is the numeric local PK — it is safe to send
//     (it is not a cross-tenant secret), but it must be kept consistent with
//     what the backend returns in TenantSummary.id.
import { apiJson } from '@/api/client'
import { targetTenantHeader } from './context'
import type { SettingsDomain, SettingsVersion } from './types'
import type { ApprovalEntityType } from '@/features/databus/api-approvals'

// ---- Approval entity type map ------------------------------------------------

/**
 * Maps each settings domain to the approval entity_type constant the
 * backend expects when calling the activate endpoint.
 *
 * Sources:
 *   - banking       → ApprovalEntityBankingSettings      = "banking_settings"
 *   - fraud         → ApprovalEntityFraudRule             = "fraud_rule"
 *   - aml           → ApprovalEntityAMLThreshold          = "aml_threshold"
 *   - case-workflow → ApprovalEntityCaseSLA               = "case_sla"
 *   - notification  → ApprovalEntityNotificationRecipient = "notification_recipient"
 *   - reporting-retention → ApprovalEntityApprovalPolicy  = "approval_policy"
 *     (see comment in tenant_settings_handler.go line ~966:
 *      "Reporting/retention changes reuse ApprovalEntityApprovalPolicy")
 *
 * Verified against internal/api/tenant_settings_handler.go
 * approvalEntityType() per domain and internal/entities/approval.go constants.
 */
export const DOMAIN_APPROVAL_ENTITY_TYPE: Record<SettingsDomain, ApprovalEntityType> = {
  banking: 'banking_settings' as ApprovalEntityType,
  fraud: 'fraud_rule' as ApprovalEntityType,
  aml: 'aml_threshold' as ApprovalEntityType,
  'case-workflow': 'case_sla' as ApprovalEntityType,
  notification: 'notification_recipient' as ApprovalEntityType,
  'reporting-retention': 'approval_policy' as ApprovalEntityType,
}

// ---- Response types ----------------------------------------------------------

export interface SettingsVersionsResponse {
  items: SettingsVersion[]
}

// ---- Fetchers ----------------------------------------------------------------

/**
 * GET /tenants/{tenant_id}/settings/{domain}
 * Returns the currently active version for the domain.
 * Returns 404 when no active version exists (render empty/CTA, not an error).
 *
 * tenant_id is the numeric local PK from TenantSummary.id.
 */
export async function getActiveSettings(
  tenantId: number,
  domain: SettingsDomain,
  targetTenantId?: string | null,
): Promise<SettingsVersion> {
  return apiJson<SettingsVersion>(
    `/tenants/${tenantId}/settings/${encodeURIComponent(domain)}`,
    { headers: targetTenantHeader(targetTenantId) },
  )
}

/**
 * GET /tenants/{tenant_id}/settings/{domain}/versions
 * Returns the full version history for the domain.
 *
 * tenant_id is the numeric local PK from TenantSummary.id.
 */
export async function listSettingsVersions(
  tenantId: number,
  domain: SettingsDomain,
  targetTenantId?: string | null,
): Promise<SettingsVersionsResponse> {
  return apiJson<SettingsVersionsResponse>(
    `/tenants/${tenantId}/settings/${encodeURIComponent(domain)}/versions`,
    { headers: targetTenantHeader(targetTenantId) },
  )
}

/**
 * POST /tenants/{tenant_id}/settings/{domain}/draft
 * Creates a new draft version.
 *
 * Body is the domain-specific payload object.
 * The backend validates field-level constraints and returns 400 with
 * error.message on validation failures (e.g. missing institution_code for
 * banking, invalid thresholds for fraud/aml).
 * AF-007: payload for notification domain may contain PII — never log.
 *
 * tenant_id is the numeric local PK from TenantSummary.id.
 */
export async function createSettingsDraft(
  tenantId: number,
  domain: SettingsDomain,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- domain-specific payload; callers are typed per-domain via VersionedSettingsScreen generic
  payload: Record<string, any>,
  targetTenantId?: string | null,
): Promise<SettingsVersion> {
  return apiJson<SettingsVersion>(
    `/tenants/${tenantId}/settings/${encodeURIComponent(domain)}/draft`,
    {
      method: 'POST',
      body: payload,
      headers: targetTenantHeader(targetTenantId),
    },
  )
}

/**
 * POST /tenants/{tenant_id}/settings/{domain}/activate
 * Promotes a draft to active. Requires an applied approval_public_id
 * when the tenant's maker-checker policy requires approval.
 *
 * Error codes to handle:
 *   422 — approval missing or not in 'applied' state
 *   409 — approval_entity_id_mismatch (approval entity_id does not match
 *          the settings row's public_id)
 *
 * tenant_id is the numeric local PK from TenantSummary.id.
 */
export async function activateSettings(
  tenantId: number,
  domain: SettingsDomain,
  params: {
    settings_row_id: number
    approval_public_id: string
  },
  targetTenantId?: string | null,
): Promise<SettingsVersion> {
  return apiJson<SettingsVersion>(
    `/tenants/${tenantId}/settings/${encodeURIComponent(domain)}/activate`,
    {
      method: 'POST',
      body: params,
      headers: targetTenantHeader(targetTenantId),
    },
  )
}
